import { useState, useEffect, useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  FormField,
  Input,
  PageHeader,
  QueryState,
  Select,
  Skeleton,
  StatCard,
  useToast,
} from '@yhelp/ui';
import type { Ticket } from '@yhelp/shared-types';
import { DashboardLayout } from '@/templates/DashboardLayout';
import { useAuth } from '@/auth';
import { useMe } from '@/hooks/useMe';
import { useAuthorizedFetch } from '@/lib/useAuthorizedFetch';
import { useTheme } from '@/theme/ThemeProvider';
import { useProductTour } from '@/components/organisms/ProductTour/ProductTour';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '@/hooks/useNotifications';
import { KB_ARTICLES } from '@/lib/kb-articles';
import { TicketListTable } from '@/components/organisms/TicketListTable';
import {
  YSEC_INTEGRATIONS,
  YSEC_QUEUES,
  countByQueue,
  filterTicketsByQueue,
  getQueueById,
  type YsecQueueId,
} from '@/lib/ysec-catalog';

const DASHBOARD_STATS = [
  { label: 'Abertos', value: '12', accent: 'yellow' as const, trend: '+2 hoje' },
  { label: 'Resolvidos hoje', value: '8', accent: 'green' as const },
  { label: 'SLA em risco', value: '3', accent: 'orange' as const },
  { label: 'Tempo médio', value: '4h 20m', accent: 'blue' as const },
];

const TREND_BARS = [40, 65, 55, 80, 70, 90, 75];

const RECENT_TICKETS = [
  {
    id: 'demo-1',
    number: 1042,
    title: 'Erro ao acessar relatório mensal',
    status: 'em_andamento' as const,
  },
  { id: 'demo-2', number: 1038, title: 'Solicitação de novo usuário', status: 'aberto' as const },
  {
    id: 'demo-3',
    number: 1031,
    title: 'Integração Slack não notifica',
    status: 'resolvido' as const,
  },
];

function NotificationIcon({ type }: { type: string }) {
  const className = 'h-5 w-5 shrink-0 text-neutral-500 dark:text-neutral-400';
  if (type === 'comment') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  if (type === 'status') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  if (type === 'assigned') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function DashboardPage() {
  return (
    <DashboardLayout pageDescription="Visão geral da operação de suporte">
      <PageHeader title="Dashboard" description="Métricas de demonstração — dados ilustrativos" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-tour="dashboard-stats">
        {DASHBOARD_STATS.map((item, i) => (
          <div
            key={item.label}
            className="list-stagger opacity-0"
            style={{ animationDelay: `${i * 70}ms` }}
          >
            <StatCard
              label={item.label}
              value={item.value}
              accent={item.accent}
              trend={item.trend}
            />
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <h3 className="font-bold text-yhelp-black dark:text-white">Tendência — 7 dias</h3>
          <p className="mt-1 text-sm text-muted">Volume de chamados abertos por dia</p>
          <div className="mt-6 flex h-40 items-end justify-between gap-2" aria-hidden="true">
            {TREND_BARS.map((h, i) => (
              <div key={i} className="flex flex-1 flex-col items-center gap-2">
                <div
                  className="w-full rounded-t-md bg-yhelp-yellow/80 dark:bg-yhelp-yellow/60"
                  style={{ height: `${h}%` }}
                />
                <span className="text-xs text-muted">
                  {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'][i]}
                </span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-bold text-yhelp-black dark:text-white">Chamados recentes</h3>
          <ul className="mt-4 divide-y divide-neutral-200 dark:divide-neutral-800">
            {RECENT_TICKETS.map((t) => (
              <li key={t.id} className="flex items-center gap-3 py-3">
                <span className="font-mono text-sm text-muted">#{t.number}</span>
                <span className="flex-1 truncate text-sm font-medium text-yhelp-black dark:text-white">
                  {t.title}
                </span>
                <Badge variant="status" status={t.status}>
                  {t.status.replace('_', ' ')}
                </Badge>
              </li>
            ))}
          </ul>
          <Link to="/tickets" className="mt-4 inline-block">
            <Button size="sm" variant="secondary">
              Ver todos os chamados
            </Button>
          </Link>
        </Card>
      </div>
    </DashboardLayout>
  );
}

export function QueuesPage() {
  const { user } = useAuth();
  const authorizedFetch = useAuthorizedFetch();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [activeQueue, setActiveQueue] = useState<YsecQueueId>('triagem_n1');
  const isAgent = user?.role !== 'requester' && user?.role !== 'viewer';

  const { data, isLoading } = useQuery({
    queryKey: ['tickets', 'queue'],
    queryFn: () => authorizedFetch<{ data: Ticket[] }>('/v1/tickets'),
    enabled: isAgent,
  });

  const tickets = useMemo(() => data?.data ?? [], [data?.data]);
  const queueCounts = useMemo(() => countByQueue(tickets), [tickets]);
  const activeQueueDef = getQueueById(activeQueue);
  const queueTickets = useMemo(
    () => filterTicketsByQueue(tickets, activeQueue),
    [tickets, activeQueue],
  );

  const assign = useMutation({
    mutationFn: (ticketId: string) => {
      setAssigningId(ticketId);
      return authorizedFetch(`/v1/tickets/${ticketId}`, {
        method: 'PATCH',
        body: JSON.stringify({ assignee_id: user?.sub, status: 'em_andamento' }),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tickets'] });
      toast({ variant: 'success', title: 'Chamado assumido' });
    },
    onSettled: () => setAssigningId(null),
  });

  if (!isAgent) {
    return (
      <DashboardLayout>
        <Alert variant="info" title="Acesso restrito">
          Filas operacionais são visíveis apenas para agentes e administradores ySec.
        </Alert>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageDescription="Filas por produto e skill — triagem N1 a N2">
      <PageHeader
        title="Filas operacionais"
        description="Selecione uma fila do portfólio ySec para priorizar o atendimento."
      />

      <div
        className="mb-4 flex flex-wrap gap-2 border-b border-neutral-200 pb-3 dark:border-neutral-800"
        role="tablist"
        aria-label="Filas operacionais"
        data-tour="queue-list"
      >
        {YSEC_QUEUES.map((q) => {
          const count = queueCounts[q.id];
          const active = activeQueue === q.id;
          return (
            <button
              key={q.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setActiveQueue(q.id)}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                active
                  ? 'border-b-2 border-yhelp-yellow bg-yhelp-yellow/10 text-yhelp-black dark:text-white'
                  : 'text-muted hover:bg-neutral-100 dark:hover:bg-neutral-800'
              }`}
            >
              {q.label}
              <span
                className={`ml-2 inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-bold ${
                  count > 0
                    ? 'bg-yhelp-yellow text-yhelp-black'
                    : 'bg-neutral-200 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {activeQueueDef ? (
        <p className="mb-4 text-sm text-muted">{activeQueueDef.description}</p>
      ) : null}

      <QueryState
        isLoading={isLoading}
        isEmpty={!isLoading && queueTickets.length === 0}
        emptyProps={{
          title: 'Fila vazia',
          description: `Nenhum chamado na fila ${activeQueueDef?.label ?? ''}.`,
          icon: (
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-950 dark:text-green-400">
              ✓
            </span>
          ),
        }}
      >
        <TicketListTable
          tickets={queueTickets}
          showAssume={activeQueue === 'triagem_n1'}
          onAssume={(id) => assign.mutate(id)}
          assumingId={assigningId}
          compact={activeQueue !== 'triagem_n1'}
        />
      </QueryState>
    </DashboardLayout>
  );
}

export function SettingsPage() {
  const { data: me } = useMe();
  const { mode, setMode } = useTheme();
  const { startTour } = useProductTour();
  const authorizedFetch = useAuthorizedFetch();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('requester');
  const [slackChannel, setSlackChannel] = useState('');
  const tenantId = me?.data.user.tenant_id;
  const canInvite = me?.data.user.role === 'tenant_admin' || me?.data.user.role === 'superadmin';
  const canIntegrate = canInvite;

  const slackStatus = useQuery({
    queryKey: ['slack-status'],
    queryFn: () =>
      authorizedFetch<{
        data: {
          connected: boolean;
          team_name?: string;
          channel_id?: string | null;
          channel_name?: string | null;
          channels?: Array<{ id: string; name: string }>;
        };
      }>('/v1/integrations/slack/status'),
    enabled: canIntegrate,
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const slack = params.get('slack');
    if (slack === 'connected') {
      toast({
        variant: 'success',
        title: 'Slack conectado',
        description: 'Escolha o canal de notificações.',
      });
      window.history.replaceState({}, '', '/settings');
    } else if (slack === 'error' || slack === 'expired') {
      toast({ variant: 'error', title: 'Erro ao conectar Slack', description: 'Tente novamente.' });
      window.history.replaceState({}, '', '/settings');
    }
  }, [toast]);

  useEffect(() => {
    if (slackStatus.data?.data.channel_id) {
      setSlackChannel(slackStatus.data.data.channel_id);
    }
  }, [slackStatus.data?.data.channel_id]);

  const saveSlackChannel = useMutation({
    mutationFn: () => {
      const channel = slackStatus.data?.data.channels?.find((c) => c.id === slackChannel);
      return authorizedFetch('/v1/integrations/slack/channel', {
        method: 'PATCH',
        body: JSON.stringify({
          channel_id: slackChannel,
          channel_name: channel?.name ?? slackChannel,
        }),
      });
    },
    onSuccess: () => {
      void slackStatus.refetch();
      toast({ variant: 'success', title: 'Canal Slack atualizado' });
    },
  });

  const disconnectSlack = useMutation({
    mutationFn: () =>
      authorizedFetch('/v1/integrations/slack', {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void slackStatus.refetch();
      toast({ variant: 'success', title: 'Slack desconectado' });
    },
  });

  const connectSlack = async () => {
    const res = await authorizedFetch<{ data: { url: string } }>('/v1/integrations/slack/install', {
      headers: { Accept: 'application/json' },
    });
    window.location.href = res.data.url;
  };

  const invite = useMutation({
    mutationFn: () =>
      authorizedFetch(`/v1/tenants/${tenantId}/invitations`, {
        method: 'POST',
        body: JSON.stringify({ email, role }),
      }),
    onSuccess: () => {
      toast({
        variant: 'success',
        title: 'Convite criado',
        description: 'Link enviado na resposta da API.',
      });
      setEmail('');
    },
    onError: () => toast({ variant: 'error', title: 'Erro ao convidar usuário' }),
  });

  return (
    <DashboardLayout pageDescription="Personalize aparência, integrações e convites">
      <PageHeader title="Configurações" />
      <div className="mx-auto max-w-lg space-y-6">
        <Card className="p-6">
          <h2 className="font-bold text-yhelp-black dark:text-white">Aparência</h2>
          <FormField id="theme" label="Tema" className="mt-4">
            <Select
              id="theme"
              value={mode}
              onChange={(e) => setMode(e.target.value as 'light' | 'dark' | 'system')}
            >
              <option value="system">Sistema</option>
              <option value="light">Claro</option>
              <option value="dark">Escuro</option>
            </Select>
          </FormField>
        </Card>

        <Card className="p-6">
          <h2 className="font-bold text-yhelp-black dark:text-white">Tour guiado</h2>
          <p className="mt-2 text-sm text-muted">
            Revise o passo a passo de criação e acompanhamento de chamados.
          </p>
          <Button className="mt-4" variant="secondary" onClick={() => startTour('welcome')}>
            Refazer tour guiado
          </Button>
        </Card>

        {canIntegrate && (
          <Card className="p-6">
            <h2 className="font-bold text-yhelp-black dark:text-white">Ecossistema ySec</h2>
            <p className="mt-2 text-sm text-muted">
              Integrações com ferramentas do portfólio ySec e parceiros.
            </p>
            <ul className="mt-4 space-y-2">
              {YSEC_INTEGRATIONS.map((item) => (
                <li
                  key={item.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-neutral-200 px-3 py-2 dark:border-neutral-800"
                >
                  <div>
                    <p className="text-sm font-semibold text-yhelp-black dark:text-white">
                      {item.label}
                    </p>
                    <p className="text-xs text-muted">{item.description}</p>
                  </div>
                  <Badge
                    variant={item.status === 'connected' ? 'status' : 'neutral'}
                    status={item.status === 'connected' ? 'resolvido' : undefined}
                  >
                    {item.status === 'connected' ? 'Ativo' : 'Em breve'}
                  </Badge>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {canIntegrate && (
          <Card className="p-6">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-bold text-yhelp-black dark:text-white">Integração Slack</h2>
              {slackStatus.data?.data.connected ? (
                <Badge variant="status" status="resolvido">
                  conectado
                </Badge>
              ) : (
                <Badge variant="neutral">desconectado</Badge>
              )}
            </div>
            <p className="mt-2 text-sm text-muted">
              Receba alertas de novos chamados, comentários e mudanças de status no Slack.
            </p>
            {slackStatus.isLoading && <Skeleton className="mt-4 h-10 w-full" />}
            {!slackStatus.isLoading && !slackStatus.data?.data.connected && (
              <Button className="mt-4" onClick={() => void connectSlack()}>
                Conectar Slack
              </Button>
            )}
            {slackStatus.data?.data.connected && (
              <div className="mt-4 space-y-3">
                <Alert
                  variant="success"
                  title={`Conectado: ${slackStatus.data.data.team_name ?? 'Slack'}`}
                />
                <FormField id="slack-channel" label="Canal de notificações">
                  <Select
                    id="slack-channel"
                    value={slackChannel}
                    onChange={(e) => setSlackChannel(e.target.value)}
                  >
                    <option value="">Selecione um canal</option>
                    {slackStatus.data.data.channels?.map((ch) => (
                      <option key={ch.id} value={ch.id}>
                        #{ch.name}
                      </option>
                    ))}
                  </Select>
                </FormField>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    loading={saveSlackChannel.isPending}
                    onClick={() => saveSlackChannel.mutate()}
                    disabled={!slackChannel}
                  >
                    Salvar canal
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={disconnectSlack.isPending}
                    onClick={() => disconnectSlack.mutate()}
                  >
                    Desconectar
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {canInvite && (
          <Card className="p-6">
            <h2 className="font-bold text-yhelp-black dark:text-white">Convidar usuário</h2>
            <form
              className="mt-4 space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                invite.mutate();
              }}
            >
              <FormField id="invite-email" label="E-mail">
                <Input
                  id="invite-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </FormField>
              <FormField id="invite-role" label="Papel">
                <Select id="invite-role" value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="requester">Solicitante</option>
                  <option value="agent_n1">Agente N1</option>
                  <option value="agent_n2">Agente N2</option>
                  <option value="tenant_admin">Admin</option>
                  <option value="viewer">Somente leitura</option>
                </Select>
              </FormField>
              <Button type="submit" loading={invite.isPending}>
                Enviar convite
              </Button>
            </form>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}

export function NotificationsPage() {
  const { data, isLoading, isError, refetch } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  return (
    <DashboardLayout pageDescription="Alertas sobre chamados e atualizações">
      <PageHeader
        title="Notificações"
        actions={
          (data?.meta.unread_count ?? 0) > 0 ? (
            <Button
              size="sm"
              variant="secondary"
              loading={markAll.isPending}
              onClick={() => markAll.mutate()}
            >
              Marcar todas
            </Button>
          ) : undefined
        }
      />
      <QueryState
        isLoading={isLoading}
        isError={isError}
        isEmpty={!isLoading && !isError && !data?.data.length}
        errorProps={{
          title: 'Erro ao carregar notificações',
          action: (
            <Button variant="secondary" onClick={() => void refetch()}>
              Tentar novamente
            </Button>
          ),
        }}
        emptyProps={{
          title: 'Sem notificações',
          description: 'Você será avisado sobre chamados e atualizações aqui.',
          icon: (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              className="h-12 w-12 text-neutral-400"
              aria-hidden="true"
            >
              <path
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          ),
        }}
      >
        <ul className="divide-y divide-neutral-200 surface-card overflow-hidden dark:divide-neutral-800">
          {data?.data.map((n, i) => (
            <li
              key={n.id}
              className={`list-stagger px-4 py-3 ${n.is_read ? 'opacity-70' : 'border-l-4 border-yhelp-yellow bg-yhelp-yellow/5 dark:bg-yhelp-yellow/10'}`}
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="flex items-start gap-3">
                <NotificationIcon type={n.type} />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-yhelp-black dark:text-white">{n.title}</p>
                  <p className="mt-1 text-sm text-muted">{n.body}</p>
                  <time className="mt-2 block text-xs text-muted">
                    {new Date(n.created_at).toLocaleString('pt-BR')}
                  </time>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-2">
                  {!n.is_read && (
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={markRead.isPending}
                      onClick={() => markRead.mutate(n.id)}
                    >
                      Marcar lida
                    </Button>
                  )}
                  {n.ticket_id && (
                    <Link to="/tickets/$ticketId" params={{ ticketId: n.ticket_id }}>
                      <Button size="sm" variant="secondary">
                        Ver chamado
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </QueryState>
    </DashboardLayout>
  );
}

export function KbPage() {
  const [search, setSearch] = useState('');
  const filtered = KB_ARTICLES.filter(
    (a) =>
      a.title.toLowerCase().includes(search.toLowerCase()) ||
      a.category.toLowerCase().includes(search.toLowerCase()) ||
      a.summary.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <DashboardLayout pageDescription="Artigos e guias para usar o yHelp">
      <PageHeader title="Base de conhecimento" />
      <Input
        className="mb-6 max-w-md"
        placeholder="Buscar artigos…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        aria-label="Buscar na base de conhecimento"
      />
      <QueryState
        isEmpty={filtered.length === 0}
        emptyProps={{
          variant: 'search',
          title: 'Nenhum artigo encontrado',
          description: 'Tente outro termo de busca.',
          action: (
            <Button variant="secondary" onClick={() => setSearch('')}>
              Limpar busca
            </Button>
          ),
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((article, i) => (
            <Link key={article.id} to="/kb/$articleId" params={{ articleId: article.id }}>
              <Card
                variant="interactive"
                className="list-stagger h-full p-5 opacity-0"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <p className="text-xs font-medium uppercase tracking-wide text-muted">
                  {article.category}
                </p>
                <h3 className="mt-2 font-bold text-yhelp-black dark:text-white">{article.title}</h3>
                <p className="mt-2 text-sm text-muted">{article.summary}</p>
              </Card>
            </Link>
          ))}
        </div>
      </QueryState>
    </DashboardLayout>
  );
}
